import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Server, RefreshCw, Search, CheckCircle, AlertCircle, Link2, Link2Off, X, User, HardDrive, Wifi, Mail, ChevronDown, ExternalLink, Download, Info } from 'lucide-react';
import { useAppContext, Customer, CustomerService } from '../../context/AppContext';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
interface CpanelAccount {
  username: string;
  domain: string;
  email: string;
  diskUsedMb: number;
  diskLimitMb: number;
  bandwidthUsedMb: number;
  bandwidthLimitMb: number;
  emailAccounts: number;
  status: 'active' | 'suspended';
  plan: string;
  ip: string;
  // Resolved after matching
  matchedCustomerId?: string;
  matchedCustomerName?: string;
}
// Simulated WHM API response — in production this would be fetched via a Supabase Edge Function
// that proxies: GET https://{host}:2087/json-api/listaccts?api.version=1
const generateMockAccounts = (serverHostname: string): CpanelAccount[] => [{
  username: 'jsmith',
  domain: 'jsmithdesign.com',
  email: 'john@jsmithdesign.com',
  diskUsedMb: 342,
  diskLimitMb: 5000,
  bandwidthUsedMb: 1200,
  bandwidthLimitMb: 50000,
  emailAccounts: 4,
  status: 'active',
  plan: 'starter',
  ip: serverHostname
}, {
  username: 'acmecorp',
  domain: 'acmecorp.net',
  email: 'admin@acmecorp.net',
  diskUsedMb: 2100,
  diskLimitMb: 10000,
  bandwidthUsedMb: 8500,
  bandwidthLimitMb: 100000,
  emailAccounts: 12,
  status: 'active',
  plan: 'business',
  ip: serverHostname
}, {
  username: 'myblog99',
  domain: 'myblog99.com',
  email: 'owner@myblog99.com',
  diskUsedMb: 80,
  diskLimitMb: 2000,
  bandwidthUsedMb: 200,
  bandwidthLimitMb: 20000,
  emailAccounts: 1,
  status: 'suspended',
  plan: 'starter',
  ip: serverHostname
}, {
  username: 'techstore',
  domain: 'techstore.io',
  email: 'info@techstore.io',
  diskUsedMb: 5600,
  diskLimitMb: 10000,
  bandwidthUsedMb: 22000,
  bandwidthLimitMb: 100000,
  emailAccounts: 8,
  status: 'active',
  plan: 'business',
  ip: serverHostname
}];
export function CpanelImporter() {
  const {
    serverConnections,
    customers,
    updateCustomer
  } = useAppContext();
  const cpanelServers = serverConnections.filter(s => s.type === 'cpanel');
  const [selectedServerId, setSelectedServerId] = useState<string>(cpanelServers[0]?.id || '');
  const [accounts, setAccounts] = useState<CpanelAccount[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'matched' | 'unmatched'>('all');
  const [assignModal, setAssignModal] = useState<{
    account: CpanelAccount;
  } | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const selectedServer = cpanelServers.find(s => s.id === selectedServerId);
  const matchAccountsToCustomers = (rawAccounts: CpanelAccount[]): CpanelAccount[] => {
    return rawAccounts.map(acc => {
      const match = customers.find(c => c.email.toLowerCase() === acc.email.toLowerCase() || c.services?.some(s => s.domain === acc.domain || s.controlPanelUsername === acc.username));
      return {
        ...acc,
        matchedCustomerId: match?.id,
        matchedCustomerName: match?.name
      };
    });
  };
  const handleFetchAccounts = async () => {
    if (!selectedServer) {
      toast.error('Please select a cPanel server first');
      return;
    }
    setIsFetching(true);
    setAccounts([]);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('whm-list-accounts', {
        body: {
          serverId: selectedServerId
        }
      });
      console.log('WHM response:', {
        data,
        error
      });
      if (error) {
        console.error('Edge Function error:', error);
        throw new Error(typeof error === 'object' && error.message ? error.message : `Edge Function returned an error: ${JSON.stringify(error)}`);
      }
      if (!data) {
        throw new Error('Edge Function returned no data. Check that the function is deployed with the WHM proxy code.');
      }
      if (data.error) {
        throw new Error(data.error + (data.details ? `: ${data.details}` : ''));
      }
      if (!data.accounts || !Array.isArray(data.accounts)) {
        throw new Error(`Unexpected response format. Expected { accounts: [...] } but got: ${JSON.stringify(data).slice(0, 200)}`);
      }
      const matched = matchAccountsToCustomers(data.accounts);
      setAccounts(matched);
      toast.success(`Fetched ${matched.length} accounts from ${selectedServer.name}`);
    } catch (err: any) {
      const message = err?.message || 'Unknown error';
      console.error('Fetch accounts failed:', message);
      toast.error(message, {
        duration: 8000
      });
    } finally {
      setIsFetching(false);
    }
  };
  const openAssignModal = (account: CpanelAccount) => {
    setAssignModal({
      account
    });
    setSelectedCustomerId(account.matchedCustomerId || '');
  };
  const handleAssign = async () => {
    if (!assignModal || !selectedCustomerId || !selectedServer) return;
    setIsSaving(true);
    try {
      const customer = customers.find(c => c.id === selectedCustomerId);
      if (!customer) throw new Error('Customer not found');
      const {
        account
      } = assignModal;
      const cpanelUrl = `https://${selectedServer.hostname}:2083`;
      // Build the new service entry
      const newService: CustomerService = {
        packageId: `cpanel_${account.username}`,
        packageName: `cPanel — ${account.domain}`,
        category: 'hosting',
        status: account.status === 'active' ? 'active' : 'suspended',
        billingCycle: 'monthly',
        price: 0,
        nextRenewal: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        domain: account.domain,
        serverType: 'cpanel',
        controlPanelUrl: cpanelUrl,
        controlPanelUsername: account.username,
        diskUsageMb: account.diskUsedMb,
        diskLimitMb: account.diskLimitMb,
        bandwidthUsedMb: account.bandwidthUsedMb,
        bandwidthLimitMb: account.bandwidthLimitMb,
        emailAccounts: account.emailAccounts
      };
      // Remove any existing service for this domain/username, then add updated
      const existingServices = customer.services || [];
      const filteredServices = existingServices.filter(s => s.controlPanelUsername !== account.username && s.domain !== account.domain);
      const updatedServices = [...filteredServices, newService];
      // Update in Supabase
      const {
        error
      } = await supabase.from('customers').update({
        services: updatedServices
      }).eq('id', selectedCustomerId);
      if (error) throw error;
      // Update local state
      updateCustomer(selectedCustomerId, {
        services: updatedServices
      });
      // Update the accounts list to reflect the match
      setAccounts(prev => prev.map(a => a.username === account.username ? {
        ...a,
        matchedCustomerId: selectedCustomerId,
        matchedCustomerName: customer.name
      } : a));
      toast.success(`Assigned ${account.domain} to ${customer.name}`);
      setAssignModal(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to assign account');
    } finally {
      setIsSaving(false);
    }
  };
  const filteredAccounts = accounts.filter(acc => {
    const matchesSearch = acc.username.toLowerCase().includes(searchQuery.toLowerCase()) || acc.domain.toLowerCase().includes(searchQuery.toLowerCase()) || acc.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || filterStatus === 'matched' && acc.matchedCustomerId || filterStatus === 'unmatched' && !acc.matchedCustomerId;
    return matchesSearch && matchesFilter;
  });
  const matchedCount = accounts.filter(a => a.matchedCustomerId).length;
  const unmatchedCount = accounts.filter(a => !a.matchedCustomerId).length;
  return <div className="space-y-8" data-id="element-148">
      <div data-id="element-149">
        <h1 className="text-2xl font-bold text-white mb-2" data-id="element-150">
          cPanel Account Importer
        </h1>
        <p className="text-slate-400" data-id="element-151">
          Pull accounts from your WHM server and link them to billing customers
          — similar to WHMCS account sync.
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3" data-id="element-152">
        <Info size={18} className="text-blue-400 shrink-0 mt-0.5" data-id="element-153" />
        <div className="text-sm text-slate-400" data-id="element-154">
          <span className="text-blue-400 font-medium" data-id="element-155">How it works: </span>
          This tool fetches all cPanel accounts from your WHM server and
          compares them against your billing customers by email and domain.
          Matched accounts are automatically linked. Unmatched accounts can be
          manually assigned to any customer. In production, account fetching is
          handled via a secure Edge Function to avoid CORS — currently showing
          simulated data.
        </div>
      </div>

      {/* Server Selector + Fetch */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6" data-id="element-156">
        <h2 className="text-lg font-bold text-white mb-4" data-id="element-157">Select Server</h2>

        {cpanelServers.length === 0 ? <div className="text-center py-8" data-id="element-158">
            <Server size={32} className="text-slate-600 mx-auto mb-3" data-id="element-159" />
            <p className="text-slate-400 mb-2" data-id="element-160">No cPanel servers connected.</p>
            <p className="text-sm text-slate-500" data-id="element-161">
              Go to <span className="text-fox-500" data-id="element-162">Server Integrations</span>{' '}
              and add a cPanel/WHM server first.
            </p>
          </div> : <div className="flex flex-col sm:flex-row gap-4" data-id="element-163">
            <div className="flex-1" data-id="element-164">
              <div className="relative" data-id="element-165">
                <select value={selectedServerId} onChange={e => setSelectedServerId(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500 appearance-none" data-id="element-166">
                  {cpanelServers.map(s => <option key={s.id} value={s.id} data-id="element-167">
                      {s.name} — {s.hostname}:{s.port}
                    </option>)}
                </select>
                <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-168" />
              </div>
            </div>
            <button onClick={handleFetchAccounts} disabled={isFetching || !selectedServerId} className="flex items-center gap-2 bg-fox-600 hover:bg-fox-500 text-white px-6 py-3 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-169">
              {isFetching ? <RefreshCw size={18} className="animate-spin" data-id="element-170" /> : <Download size={18} data-id="element-171" />}
              {isFetching ? 'Fetching...' : 'Fetch Accounts'}
            </button>
          </div>}
      </div>

      {/* Results */}
      {accounts.length > 0 && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-6" data-id="element-172">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4" data-id="element-173">
            {[{
          label: 'Total Accounts',
          value: accounts.length,
          color: 'text-white'
        }, {
          label: 'Matched',
          value: matchedCount,
          color: 'text-emerald-500'
        }, {
          label: 'Unmatched',
          value: unmatchedCount,
          color: 'text-amber-500'
        }].map(stat => <div key={stat.label} className="bg-slate-900 border border-slate-800 rounded-xl p-4 text-center" data-id="element-174">
                <div className={`text-2xl font-bold ${stat.color} mb-1`} data-id="element-175">
                  {stat.value}
                </div>
                <div className="text-xs text-slate-400" data-id="element-176">{stat.label}</div>
              </div>)}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-177">
            <div className="relative flex-1" data-id="element-178">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-179" />
              <input type="text" placeholder="Search by username, domain, or email..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-fox-500" data-id="element-180" />
            </div>
            <div className="flex gap-2" data-id="element-181">
              {(['all', 'matched', 'unmatched'] as const).map(f => <button key={f} onClick={() => setFilterStatus(f)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors capitalize ${filterStatus === f ? 'bg-fox-600 text-white' : 'bg-slate-900 border border-slate-800 text-slate-400 hover:text-white'}`} data-id="element-182">
                  {f}
                </button>)}
            </div>
          </div>

          {/* Accounts Table */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden" data-id="element-183">
            <div className="overflow-x-auto" data-id="element-184">
              <table className="w-full text-left min-w-[900px]" data-id="element-185">
                <thead data-id="element-186">
                  <tr className="border-b border-slate-800 text-slate-400 text-xs uppercase tracking-wide" data-id="element-187">
                    <th className="p-4 font-medium" data-id="element-188">Account</th>
                    <th className="p-4 font-medium" data-id="element-189">Plan</th>
                    <th className="p-4 font-medium" data-id="element-190">Disk</th>
                    <th className="p-4 font-medium" data-id="element-191">Bandwidth</th>
                    <th className="p-4 font-medium" data-id="element-192">Emails</th>
                    <th className="p-4 font-medium" data-id="element-193">Status</th>
                    <th className="p-4 font-medium" data-id="element-194">Billing Match</th>
                    <th className="p-4 font-medium" data-id="element-195">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800" data-id="element-196">
                  {filteredAccounts.map((acc, i) => {
                const diskPct = Math.round(acc.diskUsedMb / acc.diskLimitMb * 100);
                const bwPct = Math.round(acc.bandwidthUsedMb / acc.bandwidthLimitMb * 100);
                return <tr key={acc.username} className="hover:bg-slate-950/50 transition-colors" data-id="element-197">
                        <td className="p-4" data-id="element-198">
                          <div className="font-medium text-white" data-id="element-199">
                            {acc.domain}
                          </div>
                          <div className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-200">
                            {acc.username}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5" data-id="element-201">
                            {acc.email}
                          </div>
                        </td>
                        <td className="p-4" data-id="element-202">
                          <span className="text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded-lg capitalize" data-id="element-203">
                            {acc.plan}
                          </span>
                        </td>
                        <td className="p-4" data-id="element-204">
                          <div className="text-xs text-slate-400 mb-1" data-id="element-205">
                            {acc.diskUsedMb} / {acc.diskLimitMb} MB
                          </div>
                          <div className="w-24 bg-slate-800 rounded-full h-1.5" data-id="element-206">
                            <div className={`h-1.5 rounded-full ${diskPct > 90 ? 'bg-red-500' : diskPct > 75 ? 'bg-amber-500' : 'bg-fox-500'}`} style={{
                        width: `${diskPct}%`
                      }} data-id="element-207" />
                          </div>
                        </td>
                        <td className="p-4" data-id="element-208">
                          <div className="text-xs text-slate-400 mb-1" data-id="element-209">
                            {(acc.bandwidthUsedMb / 1024).toFixed(1)} /{' '}
                            {(acc.bandwidthLimitMb / 1024).toFixed(0)} GB
                          </div>
                          <div className="w-24 bg-slate-800 rounded-full h-1.5" data-id="element-210">
                            <div className={`h-1.5 rounded-full ${bwPct > 90 ? 'bg-red-500' : bwPct > 75 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{
                        width: `${bwPct}%`
                      }} data-id="element-211" />
                          </div>
                        </td>
                        <td className="p-4" data-id="element-212">
                          <div className="flex items-center gap-1.5 text-sm text-slate-300" data-id="element-213">
                            <Mail size={14} className="text-slate-500" data-id="element-214" />
                            {acc.emailAccounts}
                          </div>
                        </td>
                        <td className="p-4" data-id="element-215">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${acc.status === 'active' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`} data-id="element-216">
                            <div className={`w-1.5 h-1.5 rounded-full ${acc.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`} data-id="element-217"></div>
                            {acc.status.charAt(0).toUpperCase() + acc.status.slice(1)}
                          </span>
                        </td>
                        <td className="p-4" data-id="element-218">
                          {acc.matchedCustomerId ? <div className="flex items-center gap-2" data-id="element-219">
                              <CheckCircle size={16} className="text-emerald-500 shrink-0" data-id="element-220" />
                              <div data-id="element-221">
                                <div className="text-xs font-medium text-emerald-400" data-id="element-222">
                                  {acc.matchedCustomerName}
                                </div>
                                <div className="text-xs text-slate-500" data-id="element-223">
                                  Linked
                                </div>
                              </div>
                            </div> : <div className="flex items-center gap-2 text-amber-500" data-id="element-224">
                              <Link2Off size={16} className="shrink-0" data-id="element-225" />
                              <span className="text-xs font-medium" data-id="element-226">
                                Unmatched
                              </span>
                            </div>}
                        </td>
                        <td className="p-4" data-id="element-227">
                          <button onClick={() => openAssignModal(acc)} className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${acc.matchedCustomerId ? 'bg-slate-800 hover:bg-slate-700 text-slate-300' : 'bg-fox-600/20 hover:bg-fox-600/30 text-fox-400 border border-fox-500/20'}`} data-id="element-228">
                            <Link2 size={14} data-id="element-229" />
                            {acc.matchedCustomerId ? 'Reassign' : 'Assign'}
                          </button>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>}

      {/* Assign Modal */}
      <AnimatePresence data-id="element-230">
        {assignModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setAssignModal(null)} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" data-id="element-231" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-232">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg shadow-2xl" data-id="element-233">
                <div className="flex items-center justify-between p-6 border-b border-slate-800" data-id="element-234">
                  <div data-id="element-235">
                    <h2 className="text-lg font-bold text-white" data-id="element-236">
                      Assign cPanel Account
                    </h2>
                    <p className="text-sm text-slate-400 mt-1" data-id="element-237">
                      Link this account to a billing customer
                    </p>
                  </div>
                  <button onClick={() => setAssignModal(null)} className="text-slate-400 hover:text-white" data-id="element-238">
                    <X size={20} data-id="element-239" />
                  </button>
                </div>

                <div className="p-6 space-y-6" data-id="element-240">
                  {/* Account Summary */}
                  <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-2" data-id="element-241">
                    <div className="flex items-center gap-3" data-id="element-242">
                      <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center" data-id="element-243">
                        <Server size={18} className="text-orange-500" data-id="element-244" />
                      </div>
                      <div data-id="element-245">
                        <div className="font-medium text-white" data-id="element-246">
                          {assignModal.account.domain}
                        </div>
                        <div className="text-xs text-slate-400 font-mono" data-id="element-247">
                          {assignModal.account.username} ·{' '}
                          {assignModal.account.plan}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 pt-2" data-id="element-248">
                      <div className="text-center" data-id="element-249">
                        <div className="text-sm font-medium text-white" data-id="element-250">
                          {assignModal.account.diskUsedMb} MB
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-251">Disk Used</div>
                      </div>
                      <div className="text-center" data-id="element-252">
                        <div className="text-sm font-medium text-white" data-id="element-253">
                          {assignModal.account.emailAccounts}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-254">Emails</div>
                      </div>
                      <div className="text-center" data-id="element-255">
                        <span className={`text-xs font-medium ${assignModal.account.status === 'active' ? 'text-emerald-400' : 'text-red-400'}`} data-id="element-256">
                          {assignModal.account.status.charAt(0).toUpperCase() + assignModal.account.status.slice(1)}
                        </span>
                        <div className="text-xs text-slate-500" data-id="element-257">Status</div>
                      </div>
                    </div>
                  </div>

                  {/* Customer Selector */}
                  <div data-id="element-258">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-259">
                      Assign to Customer
                    </label>
                    <div className="relative" data-id="element-260">
                      <select value={selectedCustomerId} onChange={e => setSelectedCustomerId(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-fox-500 appearance-none" data-id="element-261">
                        <option value="" data-id="element-262">— Select a customer —</option>
                        {customers.map(c => <option key={c.id} value={c.id} data-id="element-263">
                            {c.name} ({c.email})
                          </option>)}
                      </select>
                      <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-264" />
                    </div>
                    <p className="text-xs text-slate-500 mt-2" data-id="element-265">
                      This will add a cPanel service entry to the selected
                      customer's account with login credentials and usage stats.
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-800" data-id="element-266">
                  <button onClick={() => setAssignModal(null)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors" data-id="element-267">
                    Cancel
                  </button>
                  <button onClick={handleAssign} disabled={!selectedCustomerId || isSaving} className="flex items-center gap-2 bg-fox-600 hover:bg-fox-500 text-white px-5 py-2 rounded-xl text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-268">
                    {isSaving ? <RefreshCw size={16} className="animate-spin" data-id="element-269" /> : <Link2 size={16} data-id="element-270" />}
                    {isSaving ? 'Saving...' : 'Assign Account'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}